import React, { useEffect, useRef, useState } from "react";
import { ArrowDown, Download, Github, Linkedin } from "lucide-react";
import { Button } from "./ui/button";
import portfolio from "../data/portfolio.json";

const { personalInfo } = portfolio;

const Hero = () => {
  const containerRef = useRef(null);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const onMove = (e) => {
      const { innerWidth, innerHeight } = window;
      const x = (e.clientX / innerWidth - 0.5) * 2;
      const y = (e.clientY / innerHeight - 0.5) * 2;
      setMouse({ x, y });
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  const px = (n) => `${mouse.x * n}px`;
  const py = (n) => `${mouse.y * n}px`;

  return (
    <section
      ref={containerRef}
      className="relative min-h-screen pt-32 pb-24 overflow-hidden"
    >
      {/* Grid + radial backdrop */}
      <div className="absolute inset-0 grid-bg" />
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(900px circle at 50% 0%, rgba(94,234,212,0.10), transparent 60%), radial-gradient(700px circle at 80% 50%, rgba(251,191,36,0.06), transparent 50%)",
        }}
      />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-teal-300/20 to-transparent" />

      {/* Floating 3D shapes */}
      <div
        className="absolute top-32 right-[10%] w-72 h-72 pointer-events-none"
        style={{ transform: `translate3d(${px(-20)}, ${py(-20)}, 0)` }}
      >
        <div className="relative w-full h-full float-anim">
          <div className="absolute inset-0 rounded-3xl border border-teal-300/20 spin-slow" />
          <div className="absolute inset-6 rounded-3xl border border-teal-300/15 rotate-45" />
          <div className="absolute inset-12 rounded-3xl bg-gradient-to-br from-teal-300/10 to-transparent backdrop-blur-sm" />
          <div className="absolute inset-0 rounded-3xl glow-mint opacity-40" />
        </div>
      </div>

      <div
        className="absolute bottom-24 left-[8%] w-56 h-56 pointer-events-none"
        style={{ transform: `translate3d(${px(15)}, ${py(15)}, 0)` }}
      >
        <div className="relative w-full h-full float-slow">
          <div
            className="absolute inset-0 rounded-full border border-amber-300/15"
            style={{ transform: "rotateX(60deg)" }}
          />
          <div
            className="absolute inset-4 rounded-full border border-amber-300/20"
            style={{ transform: "rotateX(60deg) rotateZ(30deg)" }}
          />
          <div className="absolute inset-0 rounded-full bg-amber-300/[0.04] backdrop-blur-sm" />
        </div>
      </div>

      {/* Small floating dots */}
      <div
        className="absolute top-[40%] left-[20%] w-2 h-2 rounded-full bg-teal-300 shadow-[0_0_20px_rgba(94,234,212,0.8)] float-anim"
        style={{ transform: `translate3d(${px(-30)}, ${py(-30)}, 0)` }}
      />
      <div
        className="absolute top-[30%] right-[35%] w-1.5 h-1.5 rounded-full bg-amber-300 shadow-[0_0_15px_rgba(251,191,36,0.8)] float-slow"
        style={{ transform: `translate3d(${px(40)}, ${py(40)}, 0)` }}
      />
      <div
        className="absolute bottom-[35%] right-[15%] w-1 h-1 rounded-full bg-white float-anim"
        style={{ transform: `translate3d(${px(-20)}, ${py(20)}, 0)` }}
      />

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-8 z-10">
          <h1
            className="font-display font-bold text-white leading-[1.05] tracking-tighter fade-up pb-2"
            style={{ fontSize: "clamp(2.75rem, 8vw, 7rem)" }}
          >
            <span className="block text-zinc-500">Hello, I'm</span>
            <span className="block text-white text-glow">
              {personalInfo.firstName}
              <span className="text-teal-300">.</span>
            </span>
            <span className="block bg-gradient-to-r from-zinc-200 via-zinc-400 to-zinc-600 bg-clip-text text-transparent">
              {personalInfo.role}
            </span>
          </h1>

          <p className="mt-8 text-lg md:text-xl text-zinc-400 max-w-2xl leading-relaxed fade-up">
            {personalInfo.tagline}. I currently work at Eleven Systems.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4 fade-up">
            <Button
              size="lg"
              onClick={() =>
                document
                  .querySelector("#projects")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              className="bg-teal-300 text-zinc-950 hover:bg-teal-200 font-medium px-7 h-12 rounded-xl"
            >
              View my work
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() =>
                document
                  .querySelector("#contact")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              className="border-zinc-700 bg-transparent text-zinc-100 hover:bg-white/5 hover:text-white px-7 h-12 rounded-xl"
            >
              Let's talk
            </Button>
            <Button
              size="lg"
              variant="outline"
              asChild
              className="border-zinc-700 bg-transparent text-zinc-100 hover:bg-white/5 hover:text-white px-7 h-12 rounded-xl"
            >
              <a href={personalInfo.cvUrl} target="_blank" rel="noreferrer">
                <Download className="w-4 h-4" />
                CV
              </a>
            </Button>

            <div className="flex items-center gap-1 ml-2">
              {[
                { Icon: Github, href: "https://github.com/robertandros" },
                { Icon: Linkedin, href: "https://www.linkedin.com/in/robert-andros-a91266132/" },
              ].map(({ Icon, href }, i) => (
                <a
                  key={i}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  className="w-10 h-10 rounded-xl border border-zinc-800 hover:border-teal-300/40 flex items-center justify-center text-zinc-400 hover:text-teal-300 hover:bg-white/5 transition-all"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Right side: stat card / 3D visual */}
        <div className="lg:col-span-4 relative z-10 hidden lg:block">
          <div
            className="relative"
            style={{ transform: `perspective(1200px) rotateY(${mouse.x * -6}deg) rotateX(${mouse.y * 6}deg)` }}
          >
            <div className="glass-strong rounded-3xl p-6 gradient-border">
              <div className="flex items-center justify-between mb-5">
                <div className="flex gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
                  <span className="w-2.5 h-2.5 rounded-full bg-zinc-700" />
                  <span className="w-2.5 h-2.5 rounded-full bg-teal-300" />
                </div>
                <span className="text-[10px] font-mono text-zinc-500">
                  ~/portfolio.tsx
                </span>
              </div>
              <div className="font-mono text-[12px] leading-[1.8] text-zinc-300">
                <div><span className="text-zinc-600 mr-3 select-none">01</span><span className="text-teal-300">const</span> <span className="text-amber-200">developer</span> = {"{"}</div>
                <div><span className="text-zinc-600 mr-3 select-none">02</span><span className="ml-3">name: <span className="text-emerald-300">'Robert Andros'</span>,</span></div>
                <div><span className="text-zinc-600 mr-3 select-none">03</span><span className="ml-3">stack: [<span className="text-emerald-300">'JS'</span>, <span className="text-emerald-300">'Angular'</span>, <span className="text-emerald-300">'React'</span>],</span></div>
                <div><span className="text-zinc-600 mr-3 select-none">04</span><span className="ml-3">location: <span className="text-emerald-300">{"'Iași, RO'"}</span>,</span></div>
                <div><span className="text-zinc-600 mr-3 select-none">05</span><span className="ml-3">focus: <span className="text-emerald-300">'web apps'</span>,</span></div>
                <div><span className="text-zinc-600 mr-3 select-none">06</span>{"};"}</div>
                <div><span className="text-zinc-600 mr-3 select-none">07</span>&nbsp;</div>
                <div><span className="text-zinc-600 mr-3 select-none">08</span>developer.<span className="text-sky-300">build</span>(<span className="text-emerald-300">'web apps'</span>);</div>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3">
              {personalInfo.stats.slice(0, 4).map((s, i) => (
                <div
                  key={i}
                  className="glass rounded-2xl px-4 py-3 hover:border-teal-300/30 transition"
                >
                  <div className="font-display text-2xl font-semibold text-white">
                    {s.value}
                  </div>
                  <div className="text-[11px] text-zinc-500 uppercase tracking-wider">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-zinc-600">
        <span className="text-[10px] font-mono uppercase tracking-[0.3em]">
          Scroll
        </span>
        <ArrowDown className="w-4 h-4 animate-bounce" />
      </div>
    </section>
  );
};

export default Hero;
